import Vue from 'vue'
import Vuex, { Module } from 'vuex'
import { notification } from 'ant-design-vue'
import axios from 'axios'
import moment from 'moment/moment'
import 'moment/locale/nl'
import router from '@/router'
import IssueModel from '@/models/IssueModel'
import Issue, { SolverEntry, SolverStatus } from '@/types/issue'
import ProjectModel from '@/models/ProjectModel'
import ls from '@/ls'
import { ServiceStates } from '@/types/serviceItem'
moment.locale('nl')

Vue.use(Vuex)
const storeName = 'issues'
export const issueStore: Module<any, any> = {
  namespaced: true,
  state: {
    issueList: {
      data: [],
      meta: {
        per_page: 25,
        total: 0
      }
    },
    currentIssue: new Issue(),
    issueFilters: [],
    activeColumns: [],
    InactiveIssueColumns: ls.get(storeName + '-columns') || [],
    loading: {
      list: false,
      item: false,
      save: false
    },
    errors: [],
    projects: [],
    buildings: [],
    employees: [],
    issueManagers: [],
    availableCompanies: [],
    availableSolvers: [],
    tags: []
  },
  mutations: {
    setCurrent (state, { currentIssue }) {
      const normalizedIssue = Object.assign(new Issue(), currentIssue)
      normalizedIssue.solvers = Array.isArray(normalizedIssue.solvers) ? normalizedIssue.solvers : []
      normalizedIssue.estimate = normalizedIssue.estimate ? moment(normalizedIssue.estimate) : null
      normalizedIssue.appointment_at = normalizedIssue.appointment_at ? moment(normalizedIssue.appointment_at) : null
      state.currentIssue = normalizedIssue
    },
    clearCurrent (state) {
      state.currentIssue = new Issue()
    },
    clearIssueList (state) {
      state.issueList = {
        data: [],
        meta: {
          per_page: 25,
          total: 0
        }
      }
    },
    setList (state, { issueList }) {
      state.issueList = issueList
    },
    setFilters (state, { filters }) {
      state.issueFilters = filters.data
    },
    setLoading (state, data) {
      state.loading[data.type] = data.state
    },
    addError (state, error) {
      if (!state.errors.includes(error)) {
        state.errors.push(error)
      }
    },
    removeError (state, error) {
      if (state.errors.includes(error)) {
        state.errors.splice(state.errors.indexOf(error), 1)
      }
    },
    setUserId (state, id) {
      state.currentIssue.user_id = id
    },
    enabledOptionsChanged (state, { enabledOptions }) {
      state.currentIssue.enabled_options = enabledOptions
    },
    setProjects (state, projects) {
      state.projects = projects.data
    },
    setBuildings (state, buildings) {
      state.buildings = buildings.data
    },
    setCompanies (state, companies) {
      state.availableCompanies = companies.data
    },
    setEmployees (state, employees) {
      state.employees = employees
    },
    setManagers (state, managers) {
      state.issueManagers = managers
    },
    setManager (state, id) {
      state.currentIssue.manager_id = id
    },
    setInactiveColumns (state, columns) {
      state.InactiveIssueColumns = columns
    },
    setSolvers (state, solvers) {
      state.availableSolvers = solvers
    },
    setEstimate (state, date) {
      state.currentIssue.estimate = date
    },
    setPriority (state, priority) {
      state.currentIssue.priority = priority
    },
    setAdditionalInfo (state, info) {
      state.currentIssue.additional_info = info
    },
    setFiles (state, files) {
      state.currentIssue.files = files
    },
    removeFile (state, index) {
      state.currentIssue.files.splice(index, 1)
    },
    setSolverId (state, id) {
      state.currentIssue.solver_id = id
    },
    setSolverCompanyId (state, id) {
      state.currentIssue.solver_company_id = id
    },
    clearSolvers (state) {
      state.availableSolvers = []
    },
    setProject (state, id) {
      state.currentIssue.project_id = id
    },
    setBuildingId (state, id) {
      state.currentIssue.building_id = id
    },
    setBuilding (state, building) {
      state.currentIssue.building = building
    },
    setStatus (state, statusId) {
      state.currentIssue.status = statusId
    },
    setRoomId (state, id) {
      state.currentIssue.room_id = id
    },
    setElementId (state, id) {
      state.currentIssue.element_id = id
    },
    setTags (state, tags) {
      state.currentIssue.tags = tags
    },
    updateIssue (state, issue) {
      const list = state.issueList?.data || []
      const idx = list.findIndex((i: Issue) => String(i.id) === String(issue.id))
      if (idx === -1) return

      const merged = { ...list[idx], ...issue }
      state.issueList.data.splice(idx, 1, merged)
      state.issueList = { ...state.issueList }
    },
    setWorkflowSolvers (state, solvers) {
      state.currentIssue.solvers = solvers
    },
    addWorkflowSolver (state, solver) {
      state.currentIssue.solvers.push(solver)
    },
    removeWorkflowSolver (state, index) {
      state.currentIssue.solvers.splice(index, 1)
    }
  },
  actions: {
    getList ({ commit }, { settings }) {
      commit('setLoading', { type: 'list', state: true })
      let issueModel = new IssueModel()

      // Handle Filters
      if (settings.activeFilters) {
        // Check if we want the archived items
        Object.keys(settings.archiveConditions).forEach(key => {
          if (settings.archiveConditions[key].length) {
            issueModel = issueModel.whereIn(key,
              Object.values(ServiceStates).filter(value => settings.archived
                ? settings.archiveConditions[key].includes(value)
                : !settings.archiveConditions[key].includes(value)))
          }
        })

        // Then check if they need to be filtered.
        Object.keys(settings.activeFilters).forEach(key => {
          if (settings.activeFilters[key].length) {
            issueModel = issueModel.whereIn(key, settings.activeFilters[key])
          }
        })
      }

      // Add search if available.
      if (settings.search) {
        issueModel = issueModel.where('search', settings.search)
      }

      // Add orderBy if sort is set.
      if (settings.sort) {
        issueModel = issueModel.orderBy(settings.sort.order === 'ascend' ? settings.sort.columnKey : '-' + settings.sort.columnKey)
      }

      if (settings.pagination) {
        issueModel = issueModel.limit(settings.pagination.pageSize).page(settings.pagination.current)
      }

      issueModel.get().then((issueList) => {
        commit('setList', { issueList: issueList })
      }).catch(() => {
        notification.error({ message: 'Fout tijdens het ophalen van medldingen!', description: 'Er is iets mis gegaan. Probeer het later nog een keer.', duration: 3 })
      }).finally(() => {
        commit('setLoading', { type: 'list', state: false })
      })
    },
    clearList ({ commit }) {
      commit('clearList')
    },
    setCurrent ({ commit }, id) {
      return new Promise((resolve, reject) => {
        commit('setLoading', { type: 'item', state: true })
        IssueModel.$find(id).then((currentIssue) => {
          commit('setCurrent', { currentIssue: currentIssue })
          resolve(currentIssue)
        }).catch((e) => {
          notification.error({
            message: 'Fout tijdens het ophalen van deze melding!',
            description: 'Er is iets mis gegaan. Probeer het later nog een keer.',
            duration: 3
          })
          reject(e)
        }).finally(() => {
          commit('setLoading', { type: 'item', state: false })
        })
      })
    },
    setEstimate ({ commit }, date) {
      commit('setEstimate', date)
    },
    setPriority ({ commit }, priority) {
      commit('setPriority', priority)
    },
    setAdditionalInfo ({ commit }, info) {
      commit('setAdditionalInfo', info)
    },
    setMedia ({ commit }, media) {
      commit('setFiles', media)
    },
    removeFile ({ commit }, index) {
      commit('removeFile', index)
    },
    setUserId ({ commit }, id) {
      commit('setUserId', id)
    },
    clearCurrent ({ commit }) {
      commit('clearCurrent')
    },
    getIssueFilters ({ commit }, data) {
      axios.post(process.env.VUE_APP_API_URL + '/issues/filters', data)
        .then((filters) => {
          commit('setFilters', { filters })
        })
        .catch(() => {
          notification.error({ message: 'Fout tijdens het ophalen van meldingen!', description: 'Er is iets mis gegaan. Probeer het later nog een keer.', duration: 3 })
        }).finally(() => {
          commit('setLoading', { type: 'list', state: false })
        })
    },
    setInactiveColumns ({ commit }, columns) {
      ls.set(storeName + '-columns', columns)
      commit('setInactiveColumns', columns)
    },
    save ({ commit }, data) {
      commit('setLoading', { type: 'save', state: true })
      // suppressed since the results work perfectly.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { has_new_message, comments, ...payload } = data
      const issueModel = new IssueModel(payload)
      issueModel.appointment_at = data.appointment_at ? moment(data.appointment_at).format('YYYY-MM-DD HH:mm:ss') : null
      issueModel.estimate = data.estimate ? moment(data.estimate).format('YYYY-MM-DD HH:mm:ss') : null

      // Save issue.
      issueModel.save().then((currentIssue) => {
        commit('setCurrent', { currentIssue: currentIssue })
        if (data.id) {
          notification.success({ message: 'Melding opgeslagen.', description: 'De melding is succesvol opgeslagen.', duration: 3 })
        } else {
          notification.success({ message: 'Melding toegevoegd.', description: 'De melding is succesvol toegevoegd.', duration: 3 })
          if (currentIssue.id) {
            router.push({ name: 'issueEdit', params: { issueId: currentIssue.id.toString() } })
          }
        }
      }).catch((e) => {
        if (e.response.status === 422) {
          commit('addError', e.response.data.errors)
          notification.error({ message: 'Fout tijdens het opslaan van deze melding!', description: 'Niet alle velden zijn correct ingevuld.', duration: 3 })
        } else {
          notification.error({ message: 'Fout tijdens het opslaan van deze melding!', description: 'Er is iets mis gegaan. Probeer het later nog een keer.', duration: 3 })
        }
      }).finally(() => {
        commit('setLoading', { type: 'save', state: false })
      })
    },
    enabledOptionsChanged ({ commit }, enabledOptions) {
      commit('enabledOptionsChanged', { enabledOptions })
    },
    getProjects ({ commit }) {
      const projectModel = new ProjectModel()
      projectModel.get().then((projects) => {
        commit('setProjects', projects)
      }).catch(() => {
        notification.error({ message: 'Fout tijdens het ophalen van projecten!', description: 'Er is iets mis gegaan. Probeer het later nog een keer.', duration: 3 })
      }).finally(() => {
        commit('setLoading', { type: 'list', state: false })
      })
    },
    getBuildings ({ commit }, projectId) {
      axios.get(`${process.env.VUE_APP_API_URL}/buildings?filter[project.id]=${projectId}`)
        .then((res) => {
          commit('setBuildings', res.data)
        })
    },
    getCompanies ({ commit }) {
      axios.get(`${process.env.VUE_APP_API_URL}/companies/all`)
        .then((res) => {
          commit('setCompanies', res.data)
        })
    },
    getEmployees ({ commit, state }) {
      axios.get(`${process.env.VUE_APP_API_URL}/users?filter[roles.name]=employee`)
        .then((res) => {
          commit('setEmployees', res.data.data)
          if (state.currentIssue.manager_id === null) {
            state.currentIssue.manager_id = res.data.data[0].id
          }
        })
    },
    addWorkflowSolver ({ commit, state }, solver) {
      if (state.currentIssue.status === ServiceStates.Afgewezen) return
      commit('addWorkflowSolver', solver)
    },
    removeWorkflowSolver ({ commit, state }, index) {
      if (state.currentIssue.status === ServiceStates.Afgewezen) return
      commit('removeWorkflowSolver', index)
    },
    reorderWorkflowSolvers ({ commit, state }, solvers) {
      if (state.currentIssue.status === ServiceStates.Afgewezen) return
      commit('setWorkflowSolvers', solvers)
    },
    startWorkflow ({ commit, state }) {
      if (state.currentIssue.status === ServiceStates.Afgewezen) return
      const solvers = [...state.currentIssue.solvers]
      const hasRunningStep = solvers.some(s => [SolverStatus.ACTIVE, SolverStatus.READY].includes(s.status))
      const firstPendingIdx = solvers.findIndex(s => s.status === SolverStatus.PENDING)

      if (!hasRunningStep && firstPendingIdx !== -1) {
        solvers[firstPendingIdx].status = SolverStatus.ACTIVE
        commit('setStatus', ServiceStates.InBehandeling)
      }
      commit('setWorkflowSolvers', solvers)
    },
    readyWorkflowStep ({ commit, state }, solver) {
      if (state.currentIssue.status === ServiceStates.Afgewezen) return
      const solvers = [...state.currentIssue.solvers]
      const idx = solvers.findIndex(s => s === solver)

      if (idx !== -1 && solvers[idx].status === SolverStatus.ACTIVE) {
        solvers[idx].status = SolverStatus.READY
        solvers[idx].ready_at = new Date().toLocaleString('nl-NL')
        commit('setStatus', ServiceStates.Gereed)
      }
      commit('setWorkflowSolvers', solvers)
    },
    nextWorkflowStep ({ commit, state }, solver) {
      if (state.currentIssue.status === ServiceStates.Afgewezen) return
      const solvers = [...state.currentIssue.solvers]
      const idx = solvers.findIndex(s => s === solver)

      if (idx !== -1 && solvers[idx].status === SolverStatus.READY) {
        solvers[idx].status = SolverStatus.DONE
        solvers[idx].completed_at = new Date().toLocaleString('nl-NL')

        const nextPendingIdx = solvers.findIndex((s, solverIndex) => solverIndex > idx && s.status === SolverStatus.PENDING)

        if (nextPendingIdx !== -1) {
          solvers[nextPendingIdx].status = SolverStatus.ACTIVE
          commit('setStatus', ServiceStates.InBehandeling)
        } else {
          commit('setStatus', ServiceStates.Opgelost)
        }
      }
      commit('setWorkflowSolvers', solvers)
    },
    rollbackWorkflowStep ({ commit, state }, solver) {
      if (state.currentIssue.status === ServiceStates.Afgewezen) return
      const solvers = [...state.currentIssue.solvers]
      const idx = solvers.findIndex(s => s === solver)

      if (idx !== -1 && solvers[idx].status === SolverStatus.READY) {
        const lastReadyIdx = solvers.map(s => s.status).lastIndexOf(SolverStatus.READY)

        if (idx === lastReadyIdx) {
          solvers[idx].status = SolverStatus.ACTIVE
          delete solvers[idx].ready_at
          commit('setStatus', ServiceStates.InBehandeling)
        }
      }
      commit('setWorkflowSolvers', solvers)
    },
    completeRunningWorkflowSolvers ({ commit, state }) {
      if (state.currentIssue.status !== ServiceStates.Opgelost) return
      const completedAt = new Date().toLocaleString('nl-NL')
      const solvers = state.currentIssue.solvers.map((solver: SolverEntry) => {
        if (![SolverStatus.ACTIVE, SolverStatus.READY].includes(solver.status)) {
          return solver
        }

        return {
          ...solver,
          status: SolverStatus.DONE,
          ready_at: solver.ready_at || completedAt,
          completed_at: completedAt
        }
      })
      commit('setWorkflowSolvers', solvers)
    },
    getManagers ({ commit, state }, projectId) {
      if (projectId === undefined) return
      axios.get(`${process.env.VUE_APP_API_URL}/members/${projectId}/mentions`)
        .then((res) => {
          commit('setManagers', res.data)
          if (state.currentIssue.manager_id === 0 && res.data.length > 0) {
            state.currentIssue.manager_id = res.data[0].id
          }
        })
    },
    getSolvers ({ commit }, companyId) {
      if (companyId === 0 || companyId === null) return
      commit('clearSolvers')
      axios.get(`${process.env.VUE_APP_API_URL}/companies/${companyId}/solvers`)
        .then((res) => {
          commit('setSolvers', res.data.data)
        })
    },
    setSolverId ({ commit }, id) {
      commit('setSolverId', id)
    },
    setSolverCompanyId ({ commit }, id) {
      commit('setSolverCompanyId', id)
    },
    clearSolvers ({ commit }) {
      commit('clearSolvers')
    },
    setProject ({ commit }, id) {
      commit('setProject', id)
    },
    setBuildingId ({ commit }, id) {
      commit('setBuildingId', id)
    },
    setRoomId ({ commit }, id) {
      commit('setRoomId', id)
    },
    setElementId ({ commit }, id) {
      commit('setElementId', id)
    },
    setBuilding ({ commit }, building) {
      commit('setBuilding', building)
    },
    setStatus ({ commit }, statusId) {
      commit('setStatus', statusId)
    },
    setManager ({ commit }, id) {
      commit('setManager', id)
    },
    setTags ({ commit }, tags) {
      commit('setTags', tags)
    },
    updateList ({ commit }, issue) {
      commit('updateIssue', issue)
    }
  },
  getters: {
    list: state => state.issueList,
    current: state => state.currentIssue,
    filters: state => state.issueFilters,
    loading: state => state.loading,
    errors: state => state.errors,
    activeColumns: state => state.activeColumns,
    inactiveColumns: state => state.InactiveIssueColumns,
    companies: state => state.availableCompanies,
    projects: state => state.projects,
    buildings: state => state.buildings,
    managers: state => state.issueManagers,
    employees: state => state.employees,
    SolverEntries: state => state.currentIssue.solvers || [],
    solvers: state => state.availableSolvers
  }
}
